import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { getSubscriptionVehicleStatusesApi } from '@/api/subscription';
import { getValidWarranties } from '@/components/pages/WarrantyManagement/WarrantyManagementHelper';
import { Paper } from '@/components/uiParts/Card';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { Car } from '@/types/api/customerApi';
import {
  SubscriptionStatus,
  SubscriptionVehicleState,
  SubscriptionVehicleStateType,
} from '@/types/api/subscription';
import { storeDispatch } from '@/utils/store';

const stateLabels: Record<SubscriptionVehicleStateType, string> = {
  none: '申込可能',
  active: '加入済み',
  applying: '申込手続き中',
  initial_paid: '支払い方法登録待ち',
  vehicle_registration_required: '車両登録待ち',
  cancel_requested: '解約申込中',
  blocked_by_pending: '他の申込手続き中',
};

const disabledStates: SubscriptionVehicleStateType[] = [
  'active',
  'cancel_requested',
];

export const SubscriptionCarSelectPage: FC = () => {
  const authInfo = useStore(store, 'authInfo') as any;
  const ownedCars = useStore(store, 'carsInfo') as Car[];
  const [vehicleStates, setVehicleStates] = useState<
    SubscriptionVehicleState[]
  >([]);
  const [pendingWithoutCar, setPendingWithoutCar] =
    useState<SubscriptionStatus | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchVehicleStates = async () => {
      if (!authInfo?.m_customer_id) return;
      try {
        const res = await getSubscriptionVehicleStatusesApi(
          authInfo.m_customer_id,
        );
        if (res.data.success && res.data.data) {
          setVehicleStates(res.data.data.vehicle_states || []);
          setPendingWithoutCar(res.data.data.pending_without_car || null);
        }
      } catch {
        // 状態取得に失敗した場合は未加入扱いで表示し、申込API側のガードに委ねる
      } finally {
        setIsLoaded(true);
      }
    };
    fetchVehicleStates();
  }, [authInfo?.m_customer_id]);

  const warrantyCarIds = useMemo(
    () =>
      new Set(
        (ownedCars || [])
          .filter((car) => getValidWarranties(car.warranties || []).length > 0)
          .map((car) => car.t_stock_car_id)
          .filter(Boolean),
      ),
    [ownedCars],
  );

  const stateByCarId = useMemo(() => {
    const map = new Map<number, SubscriptionVehicleState>();
    vehicleStates.forEach((state) => {
      if (state.t_stock_car_id) {
        map.set(Number(state.t_stock_car_id), state);
      }
    });
    return map;
  }, [vehicleStates]);

  const getCarState = (car: Car): SubscriptionVehicleState => {
    const stockCarId = Number(car.t_stock_car_id);
    const existing = stateByCarId.get(stockCarId);
    if (existing) return existing;

    if (pendingWithoutCar) {
      return {
        t_stock_car_id: stockCarId,
        state: 'blocked_by_pending',
        can_apply: false,
        next_action:
          pendingWithoutCar.apply_status === 2
            ? 'resume_payment'
            : 'resume_application',
        subscription: pendingWithoutCar,
      };
    }

    return {
      t_stock_car_id: stockCarId,
      state: 'none',
      can_apply: true,
      next_action: 'apply',
      subscription: null,
    };
  };

  const navigateToSubscription = async (car: Car) => {
    await storeDispatch('setCarItemInfo', 'carItemInfo', car);
    f7.view.main.router.navigate(paths.subscriptionDetail);
  };

  const navigateBySubscription = (subscription: SubscriptionStatus | null) => {
    if (!subscription) return;
    const props = { subscriptionId: subscription.t_subscription_id };
    if (subscription.apply_status === 2) {
      f7.view.main.router.navigate(paths.subscriptionPayment, { props });
      return;
    }
    f7.view.main.router.navigate(paths.subscriptionComplete, { props });
  };

  const handleSelectCar = async (car: Car) => {
    if (warrantyCarIds.has(car.t_stock_car_id)) {
      f7.dialog.alert(
        'この車両は保証サービス加入済みのため、サブスクリプションサービスに申し込むことができません。',
      );
      return;
    }

    const carState = getCarState(car);
    if (carState.state === 'none') {
      await navigateToSubscription(car);
      return;
    }

    if (
      carState.state === 'applying' ||
      carState.state === 'initial_paid' ||
      carState.state === 'vehicle_registration_required' ||
      carState.state === 'blocked_by_pending'
    ) {
      navigateBySubscription(carState.subscription);
      return;
    }

    f7.dialog.alert(
      'この車両は現在サブスクリプションサービスに申し込めません。',
    );
  };

  const handleClickManualApply = () => {
    f7.view.main.router.navigate(paths.subscriptionConfirm, {
      props: {
        initialCarSelectMode: 'manual',
      },
    });
  };

  const allCars: Car[] = ownedCars || [];

  return (
    <PageTemplate
      pageName="subscription-car-select"
      title="車両選択"
      showNavBack
    >
      <div style={{ padding: '16px' }}>
        <Paper cardCN="u-margin-top-0" cardProps={{ style: { margin: 0 } }}>
          <div style={{ marginBottom: '16px' }}>
            <p
              style={{
                fontSize: '14px',
                color: '#666',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              サブスクリプションサービスに申し込む車両を選択してください。
            </p>
          </div>

          {!isLoaded && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              読み込み中...
            </div>
          )}

          {isLoaded && allCars.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              {allCars.map((car) => {
                const hasWarranty = warrantyCarIds.has(car.t_stock_car_id);
                const carState = getCarState(car);
                const disabled =
                  hasWarranty || disabledStates.includes(carState.state);
                const badgeText = hasWarranty
                  ? '保証サービス加入済み'
                  : stateLabels[carState.state];
                const badgeColor =
                  carState.state === 'none' ? '#2E7D32' : '#E65100';

                return (
                  <div
                    key={car.m_car_id || car.t_stock_car_id}
                    onClick={() => handleSelectCar(car)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      marginBottom: '8px',
                      backgroundColor: disabled ? '#f5f5f5' : '#fff',
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0',
                      cursor: disabled ? 'default' : 'pointer',
                      opacity: disabled ? 0.65 : 1,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          marginBottom: '4px',
                        }}
                      >
                        {car.maker_nm} {car.car_nm}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        {car.class_no} {car.hiragana} {car.registry_no}
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: badgeColor,
                          marginTop: '4px',
                        }}
                      >
                        {badgeText}
                      </div>
                    </div>
                    {!disabled && (
                      <div style={{ color: '#ccc', fontSize: '20px' }}>›</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={handleClickManualApply}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1E55E6',
              backgroundColor: '#fff',
              border: '2px dashed #1E55E6',
              borderRadius: '12px',
              cursor: 'pointer',
            }}
          >
            メーカー・車名を選んで新しい車両で申し込む
          </button>
        </Paper>
      </div>
    </PageTemplate>
  );
};
